import type { Container } from "pixi.js";
import type { PresentationTaskRunner } from "../../runner/PresentationTaskRunner";
import type { TweenEasing } from "../../runner/Easing";
import type { SymbolLandEffect, SymbolLandAnimationRequest } from "./SymbolLandEffect";
import type { SquashLandTuning } from "./SquashLandTuning";

interface ActiveSquash {
  node: Container;
  /** Rest scale + position captured before the squash; restored on finish. */
  base: { x: number; y: number };
  basePos: { x: number; y: number };
  /** Bumped per play; a stale in-flight squash stops writing (cancellation token). */
  gen: number;
}

/**
 * A squash-and-settle pop when a targeted symbol lands: the cell widens and flattens, then settles
 * back to its rest scale — one scale chain on engine time. Symbols outside `targetSymbolIds` are
 * skipped. The sprite-based land look for a title without Spine symbols.
 */
export class SquashSymbolLandEffect implements SymbolLandEffect {
  private readonly targetSymbolIds: ReadonlySet<string>;
  private readonly tuning: SquashLandTuning;
  private readonly taskRunner: PresentationTaskRunner;
  /** index → in-flight squash (a new landing on the cell, or `finishAll`, snaps it to rest). */
  private readonly active: Map<number, ActiveSquash> = new Map();
  private generation = 0;

  public constructor(input: { targetSymbolIds: ReadonlySet<string>; tuning: SquashLandTuning; taskRunner: PresentationTaskRunner }) {
    this.targetSymbolIds = input.targetSymbolIds;
    this.tuning = input.tuning;
    this.taskRunner = input.taskRunner;
  }

  public play(request: SymbolLandAnimationRequest): void {
    const node = request.cellNode;
    if (!this.targetSymbolIds.has(request.symbolId) || node.destroyed) return;
    this.finish(request.index); // supersede any in-flight squash on the same cell

    const base = { x: node.scale.x, y: node.scale.y };
    const basePos = { x: node.position.x, y: node.position.y };
    const t = this.tuning;
    const squashScale = { x: base.x * t.squashScaleX, y: base.y * t.squashScaleY };
    // The offset at peak squash shifts WHERE it squeezes toward (a negative Y keeps the bottom planted).
    const squashPos = { x: basePos.x + t.squashOffsetX, y: basePos.y + t.squashOffsetY };

    const gen = ++this.generation;
    this.active.set(request.index, { node, base, basePos, gen });
    const isCurrent = (): boolean => this.active.get(request.index)?.gen === gen && !node.destroyed;

    /** One leg of the chain: scale + position from → to on the runner (t01 may overshoot, e.g. backOut). */
    const leg = (fromScale: { x: number; y: number }, toScale: { x: number; y: number }, fromPos: { x: number; y: number }, toPos: { x: number; y: number }, seconds: number, easing: TweenEasing): Promise<void> =>
      this.taskRunner.tweenProgress(
        Math.max(0.001, seconds),
        (t01) => {
          if (!isCurrent()) return;
          node.scale.set(fromScale.x + (toScale.x - fromScale.x) * t01, fromScale.y + (toScale.y - fromScale.y) * t01);
          node.position.set(fromPos.x + (toPos.x - fromPos.x) * t01, fromPos.y + (toPos.y - fromPos.y) * t01);
        },
        undefined,
        { easing },
      );

    void (async () => {
      await leg(base, squashScale, basePos, squashPos, t.squashSeconds, t.squashEasing);
      if (!isCurrent()) return;
      await leg(squashScale, base, squashPos, basePos, t.settleSeconds, t.settleEasing);
      if (!isCurrent()) return;
      node.scale.set(base.x, base.y);
      node.position.set(basePos.x, basePos.y);
      this.active.delete(request.index);
    })();
  }

  public finishAll(): void {
    for (const index of Array.from(this.active.keys())) this.finish(index);
  }

  /** Snap one cell back to rest; deleting the entry cancels its in-flight chain (isCurrent → false). */
  private finish(index: number): void {
    const entry = this.active.get(index);
    if (!entry) return;
    this.active.delete(index);
    if (!entry.node.destroyed) {
      entry.node.scale.set(entry.base.x, entry.base.y);
      entry.node.position.set(entry.basePos.x, entry.basePos.y);
    }
  }
}
